import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthService } from '../services/auth'
import { UserService } from '../services/users'
import { VideoService } from '../services/videos'
import type { AuthUser } from '../services/auth'
import type { User } from '../services/users'
import AdminUserManager from '../components/AdminUserManager'
import StudentManager from '../components/StudentManager'
import TeacherManager from '../components/TeacherManager'
import SchoolManager from '../components/SchoolManager'
import VideoManager from '../components/VideoManager'
import CategoryManager from '../components/CategoryManager'
import TagManager from '../components/TagManager'
import ChatPromptManager from '../components/ChatPromptManager'
import ConversationMonitor from '../components/ConversationMonitor'
import AdminSettings from '../components/AdminSettings'
import FeaturedVideoManager from '../components/FeaturedVideoManager'
import AnnouncementManager from '../components/AnnouncementManager'
import SummaryGenerationManager from '../components/SummaryGenerationManager'
import AdminHomeworkManager from '../components/AdminHomeworkManager'
import HomeworkGradingCriteriaManager from '../components/HomeworkGradingCriteriaManager'
import AiSettingsManager from '../components/AiSettingsManager'

const AdminPage = () => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [activeMainTab, setActiveMainTab] = useState('school')
  const [activeTab, setActiveTab] = useState('students')
  const [isLoading, setIsLoading] = useState(true)
  const [users, setUsers] = useState<User[]>([])
  const [videoStats, setVideoStats] = useState<any>(null)
  const [userStats, setUserStats] = useState<any>(null)
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'MiraPASS管理システム'

    const checkAuth = async () => {
      try {
        const currentUser = await AuthService.getCurrentUser()
        
        if (!currentUser) {
          // Try to setup admin account if user is authenticated but not in database
          try {
            const setupResult = await AuthService.setupAdminAccount()
            if (setupResult?.success) {
              // Retry getting current user after setup
              const retryUser = await AuthService.getCurrentUser()
              if (retryUser && (retryUser.role === 'admin' || retryUser.role === 'super_admin')) {
                setUser(retryUser)
                await loadDashboardData()
                return
              }
            }
          } catch (setupError) {
            console.warn('Admin setup failed:', setupError)
          }
          
          navigate('/admin/login')
          return
        }

        if (currentUser.role !== 'admin' && currentUser.role !== 'super_admin') {
          navigate('/admin/login')
          return
        }

        setUser(currentUser)
        
        // Ensure admin user is properly set up in database
        try {
          await AuthService.ensureAdminUserExists()
        } catch (adminSetupError) {
          console.warn('Admin user setup warning:', adminSetupError)
          // Continue anyway - user is already validated
        }
        
        // Load dashboard data
        await loadDashboardData()
      } catch (error) {
        console.error('Auth check failed:', error)
        navigate('/admin/login')
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [navigate])

  const loadDashboardData = async () => {
    try {
      const [usersData, videoStatsData, userStatsData] = await Promise.all([
        UserService.getUsers({ limit: 10 }),
        VideoService.getVideoStats(),
        UserService.getUserStats()
      ])
      
      setUsers(usersData)
      setVideoStats(videoStatsData)
      setUserStats(userStatsData)
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    }
  }

  const handleLogout = async () => {
    try {
      await AuthService.logAdminAction('logout', 'admin_session')
      await AuthService.signOut()
      navigate('/admin/login')
    } catch (error) {
      console.error('Logout error:', error)
      navigate('/admin/login')
    } finally {
      window.scrollTo(0, 0)
    }
  }

  const TAB_GROUPS = [
    {
      key: 'school',
      label: '学校管理',
      subTabs: [
        { key: 'students', label: '生徒管理' },
        { key: 'teachers', label: '先生管理' },
        { key: 'schools', label: '学校管理' },
      ],
    },
    {
      key: 'content',
      label: 'コンテンツ管理',
      subTabs: [
        { key: 'content', label: 'コンテンツ' },
        { key: 'categories', label: 'カテゴリー' },
        { key: 'tags', label: 'タグ' },
        { key: 'featured', label: '特集' },
        { key: 'chat-prompts', label: 'みらいワーク' },
      ],
    },
    {
      key: 'homework',
      label: '宿題管理',
      subTabs: [
        { key: 'homework-system', label: 'システム宿題' },
        { key: 'homework-entre', label: 'アントレ部宿題' },
        { key: 'homework-grading', label: '採点・システム基準' },
      ],
    },
    {
      key: 'system',
      label: 'システム管理',
      subTabs: [
        { key: 'users', label: '管理者管理' },
        { key: 'conversations', label: 'チャット監視' },
        { key: 'analytics', label: 'アナリティクス' },
        { key: 'announcements', label: 'お知らせ管理' },
        { key: 'summary-generation', label: 'サマリー生成' },
        { key: 'ai-settings', label: 'AI設定' },
        { key: 'settings', label: 'システム設定' },
      ],
    },
  ]

  const handleMainTabClick = (groupKey: string, firstSubKey: string) => {
    setActiveMainTab(groupKey)
    setActiveTab(firstSubKey)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">MiraPASS管理システム</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                ようこそ、{user.name}さん
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        {/* Main Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {TAB_GROUPS.map((group) => (
              <button
                key={group.key}
                onClick={() => handleMainTabClick(group.key, group.subTabs[0]?.key ?? group.key)}
                className={`py-3 px-1 border-b-2 font-semibold text-sm ${
                  activeMainTab === group.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {group.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Sub-Tabs */}
        {(() => {
          const activeGroup = TAB_GROUPS.find(g => g.key === activeMainTab)
          if (!activeGroup || activeGroup.subTabs.length === 0) {
            return (
              <div className="border-b border-gray-200 mb-8">
                <div className="py-2 text-sm invisible" aria-hidden="true">x</div>
              </div>
            )
          }
          return (
            <div className="border-b border-gray-200 mb-8 bg-gray-50">
              <nav className="-mb-px flex space-x-6 px-2">
                {activeGroup.subTabs.map((sub) => (
                  <button
                    key={sub.key}
                    onClick={() => setActiveTab(sub.key)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === sub.key
                        ? 'border-blue-400 text-blue-500'
                        : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {sub.label}
                  </button>
                ))}
              </nav>
            </div>
          )
        })()}

        {/* Content Area */}
        <div className="bg-white rounded-lg shadow">
          {activeTab === 'users' && (
            <div className="p-6">
              <AdminUserManager />
            </div>
          )}

          {activeTab === 'users-old' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">ユーザー管理</h2>
                <button 
                  onClick={loadDashboardData}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  更新
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ユーザー名
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        メールアドレス
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        権限
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        学年・クラス
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        登録日
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        最終ログイン
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length > 0 ? (
                      users.map((userData) => (
                        <tr key={userData.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {userData.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {userData.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              userData.role === 'super_admin' 
                                ? 'bg-purple-100 text-purple-800'
                                : userData.role === 'admin'
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {userData.role === 'super_admin' ? 'スーパー管理者' : 
                               userData.role === 'admin' ? '管理者' : '生徒'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {userData.grade && userData.class ? `${userData.grade} ${userData.class}` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(userData.created_at).toLocaleDateString('ja-JP')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {userData.last_login 
                              ? new Date(userData.last_login).toLocaleDateString('ja-JP')
                              : '未ログイン'
                            }
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                          {isLoading ? '読み込み中...' : 'ユーザーがいません'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'students' && (
            <div className="p-6">
              <StudentManager />
            </div>
          )}

          {activeTab === 'teachers' && (
            <div className="p-6">
              <TeacherManager />
            </div>
          )}

          {activeTab === 'schools' && (
            <div className="p-6">
              <SchoolManager />
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="p-6">
              <CategoryManager />
            </div>
          )}

          {activeTab === 'tags' && (
            <div className="p-6">
              <TagManager />
            </div>
          )}

          {activeTab === 'content' && (
            <div className="p-6">
              <VideoManager />
            </div>
          )}

          {activeTab === 'featured' && (
            <div className="p-6">
              <FeaturedVideoManager />
            </div>
          )}

          {activeTab === 'chat-prompts' && (
            <div className="p-6">
              <ChatPromptManager />
            </div>
          )}

          {activeTab === 'conversations' && (
            <div className="p-6">
              <ConversationMonitor />
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">アナリティクス</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-900">総ユーザー数</h3>
                  <p className="text-2xl font-bold text-blue-900">
                    {userStats?.total || 0}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    新規登録 (30日): {userStats?.newRegistrations?.last30Days || 0}
                  </p>
                </div>
                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-sm font-medium text-green-900">月間アクティブユーザー</h3>
                  <p className="text-2xl font-bold text-green-900">
                    {userStats?.activeUsers?.last30Days || 0}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    週間アクティブ: {userStats?.activeUsers?.last7Days || 0}
                  </p>
                </div>
                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-900">総動画数</h3>
                  <p className="text-2xl font-bold text-purple-900">
                    {videoStats?.totalVideos || 0}
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    視聴回数: {videoStats?.totalViews || 0}
                  </p>
                </div>
                <div className="bg-orange-50 p-6 rounded-lg">
                  <h3 className="text-sm font-medium text-orange-900">完了動画数</h3>
                  <p className="text-2xl font-bold text-orange-900">
                    {videoStats?.completedVideos || 0}
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    完了率: {videoStats?.averageCompletion?.toFixed(1) || 0}%
                  </p>
                </div>
              </div>
              
              {/* User Role Statistics */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-base font-medium text-gray-900 mb-4">ユーザー役割別統計</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {userStats?.byRole?.student || 0}
                    </p>
                    <p className="text-sm text-gray-600">生徒</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {userStats?.byRole?.admin || 0}
                    </p>
                    <p className="text-sm text-gray-600">管理者</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {userStats?.byRole?.super_admin || 0}
                    </p>
                    <p className="text-sm text-gray-600">スーパー管理者</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'announcements' && (
            <div className="p-6">
              <AnnouncementManager />
            </div>
          )}

          {activeTab === 'summary-generation' && (
            <SummaryGenerationManager />
          )}

          {activeTab === 'settings' && (
            <AdminSettings />
          )}

          {(activeTab === 'homework-system' || activeTab === 'homework-entre') && (
            <AdminHomeworkManager type={activeTab === 'homework-entre' ? 'entre' : 'system'} />
          )}

          {activeTab === 'homework-grading' && (
            <HomeworkGradingCriteriaManager />
          )}

          {activeTab === 'ai-settings' && (
            <AiSettingsManager />
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminPage